/**
 * A visão Árvore: o grafo do livro-jogo.
 *
 * <p>A Lista responde "o que existe nesta área". Esta responde a outra pergunta, que a
 * Lista não sabe responder: <b>por onde se anda</b>. A hierarquia diz o que está dentro
 * do quê; as escolhas dizem para onde se vai — e as duas não coincidem, porque uma
 * opção na Forja pode levar à Taverna, que é irmã dela e não filha.
 *
 * <p>Por isso são dois traços: <b>curva sólida</b> para a hierarquia, <b>curva
 * tracejada dourada</b> para a escolha. Ver as duas sobrepostas é a única forma de
 * enxergar o livro-jogo como grafo, e não como índice.
 *
 * <p><b>Nasce recolhida nas regiões.</b> Aberta, a árvore cresce em largura: com as 49
 * áreas e as cenas dentro delas, ela passa de dez mil pixels, e uma tela dessas não é
 * um mapa — é um deserto onde se rola à procura de algo. Recolhida, cabe na janela, e
 * quem procura abre o ramo que interessa.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { CenaNaArvore, PaginaDeCenas, TipoDeCena } from './cenasTipos';
import { COR_DO_TIPO, ROTULO_DO_TIPO, TIPOS_DE_CENA } from './cenasTipos';

/** Medidas do cartão e do espaço entre eles. */
const LARGURA = 172;
const ALTURA = 46;
const VAO_X = 26;
const VAO_Y = 76;

const COR_RAIZ = '#E6C787';
const COR_REGIAO = '#C9A25A';
const COR_AREA = '#C9A25A';

interface No {
  chave: string;
  rotulo: string;
  cor: string;
  /** Rótulo do badge: REGIÃO, ÁREA ou o tipo da cena. */
  selo: string;
  cenaId?: number;
  codigo?: string;
  nota?: string | null;
  aviso?: boolean;
  destinos: number[];
  filhos: No[];
  // preenchidos pelo posicionamento
  x: number;
  y: number;
  ocultos: number;
}

interface Props {
  pagina: PaginaDeCenas;
  selecionada: number | null;
  aoSelecionar: (id: number) => void;
  busca: string;
  tiposAtivos: Set<TipoDeCena>;
  /** Recolhidos por chave. Vive no pai para o "abrir tudo" da barra alcançá-lo. */
  recolhidos: Record<string, boolean>;
  aoRecolher: (chave: string, recolhido: boolean) => void;
}

export function VisaoArvore({
  pagina,
  selecionada,
  aoSelecionar,
  busca,
  tiposAtivos,
  recolhidos,
  aoRecolher,
}: Props) {
  const palco = useRef<HTMLDivElement>(null);
  const [z, setZ] = useState(1);
  const [px, setPx] = useState(0);
  const [py, setPy] = useState(40);
  const [arrastando, setArrastando] = useState(false);
  const ajustou = useRef(false);
  /**
   * Onde estava o cartão que acabou de ser aberto ou fechado.
   *
   * <p>Abrir um ramo reposiciona a árvore inteira — os irmãos à direita são empurrados,
   * e o pai se recentraliza sobre os filhos novos. Sem compensar isso, o cartão que a
   * pessoa clicou salta para fora da tela, e o gesto parece ter quebrado a página em vez
   * de abrir um galho. Guardando a posição antes, dá para mover a câmera do mesmo tanto
   * depois: o cartão fica parado, e a árvore é que cresce em volta dele.
   */
  const ancora = useRef<{ chave: string; x: number; y: number } | null>(null);

  const termo = busca.trim().toLowerCase();

  /**
   * Um ramo está recolhido?
   *
   * <p>Sem escolha registrada, a <b>região nasce fechada</b> e o resto nasce aberto:
   * é o que faz a árvore caber na janela na primeira vez, em vez de abrir com dez mil
   * pixels de largura. Buscando, tudo abre — esconder o resultado atrás de um ramo
   * fechado transformaria a busca em adivinhação.
   */
  const estaRecolhido = useCallback(
    (chave: string) => (termo ? false : (recolhidos[chave] ?? chave.startsWith('r'))),
    [recolhidos, termo]
  );

  /* ------------------------------------------------------------- a árvore */

  const raiz = useMemo(() => {
    const combina = (c: CenaNaArvore): boolean => {
      if (!tiposAtivos.has(c.tipo)) return false;
      if (!termo) return true;
      return (
        c.nome.toLowerCase().includes(termo) ||
        (c.codigo ?? '').toLowerCase().includes(termo) ||
        (c.alvo ?? '').toLowerCase().includes(termo)
      );
    };
    const naSubarvore = (c: CenaNaArvore): boolean =>
      combina(c) || c.filhas.some(naSubarvore);

    const daCena = (c: CenaNaArvore): No => ({
      chave: `c${c.alvoColecao}-${c.alvoId ?? c.id}`,
      rotulo: c.nome,
      cor: COR_DO_TIPO[c.tipo],
      selo: ROTULO_DO_TIPO[c.tipo],
      // Nó ainda não escrito não tem cena para abrir — o clique dele é na Lista.
      cenaId: c.id ?? undefined,
      codigo: c.codigo,
      // O aviso mudou de assunto: era "esta cena não aponta para nada", que virou
      // impossível quando o nó passou a ser a própria ficha. Agora é "isto ainda não
      // foi escrito", que é o que a Árvore precisa destacar num mapa de percurso.
      nota: c.escrita ? (c.local ?? null) : 'sem texto',
      aviso: !c.escrita,
      // Nulos são escolhas sem destino decidido: não viram curva.
      destinos: c.destinos.filter((d): d is number => d != null),
      filhos: c.filhas.filter(naSubarvore).map(daCena),
      x: 0,
      y: 0,
      ocultos: 0,
    });

    const regioes: No[] = pagina.regioes
      .map((r) => {
        const areas = r.areas
          .map((a) => ({
            chave: `a${a.id}`,
            rotulo: a.nome,
            cor: COR_AREA,
            selo: 'ÁREA',
            destinos: [] as number[],
            filhos: a.cenas.filter(naSubarvore).map(daCena),
            nota: null,
            x: 0,
            y: 0,
            ocultos: 0,
          }))
          // Buscando, área sem resultado sai — fora da busca, o hexágono vazio fica,
          // porque é ele que mostra o que ainda não tem cena.
          .filter((a) => !termo || a.filhos.length > 0);

        return {
          chave: `r${r.id}`,
          rotulo: r.nome,
          cor: r.corMapa || COR_REGIAO,
          selo: 'REGIÃO',
          destinos: [] as number[],
          filhos: areas,
          nota: null,
          x: 0,
          y: 0,
          ocultos: 0,
        };
      })
      .filter((r) => !termo || r.filhos.length > 0);

    const soltas = pagina.semArea.filter(naSubarvore).map(daCena);

    return {
      chave: 'ROOT',
      rotulo: 'Mistik Island',
      cor: COR_RAIZ,
      selo: 'ILHA',
      destinos: [] as number[],
      filhos: [...regioes, ...soltas],
      nota: null,
      x: 0,
      y: 0,
      ocultos: 0,
    } as No;
  }, [pagina, termo, tiposAtivos]);

  /* -------------------------------------------------------- posicionamento */

  const { todos, largura, altura } = useMemo(() => {
    const todos: No[] = [];
    let cursor = 0;

    const posicionar = (no: No, profundidade: number) => {
      no.y = profundidade * (ALTURA + VAO_Y);
      const abre = !estaRecolhido(no.chave) && no.filhos.length > 0;
      if (!abre) {
        // Folha (ou ramo fechado): ocupa a próxima coluna livre.
        no.x = cursor;
        cursor += LARGURA + VAO_X;
        no.ocultos = no.filhos.length;
      } else {
        no.filhos.forEach((f) => posicionar(f, profundidade + 1));
        // O pai fica no meio dos filhos — é o que faz a árvore parecer uma árvore.
        no.x = (no.filhos[0].x + no.filhos[no.filhos.length - 1].x) / 2;
        no.ocultos = 0;
      }
      todos.push(no);
    };
    posicionar(raiz, 0);

    return {
      todos,
      largura: Math.max(...todos.map((n) => n.x)) + LARGURA + 60,
      altura: Math.max(...todos.map((n) => n.y)) + ALTURA + 60,
    };
  }, [raiz, estaRecolhido]);

  /** Onde cada cena visível está, para as curvas das escolhas acharem o destino. */
  const posPorCena = useMemo(() => {
    const mapa = new Map<number, No>();
    todos.forEach((n) => {
      if (n.cenaId != null) mapa.set(n.cenaId, n);
    });
    return mapa;
  }, [todos]);

  const visiveis = useMemo(() => {
    const dentro = new Set<string>();
    const marcar = (no: No) => {
      dentro.add(no.chave);
      if (estaRecolhido(no.chave)) return;
      no.filhos.forEach(marcar);
    };
    marcar(raiz);
    return todos.filter((n) => dentro.has(n.chave));
  }, [todos, raiz, estaRecolhido]);

  /* --------------------------------------------------------------- ajuste */

  function ajustar() {
    const caixa = palco.current?.getBoundingClientRect();
    if (!caixa || !largura) return;
    const escala = Math.min((caixa.width - 80) / largura, (caixa.height - 80) / altura, 1.3);
    setZ(escala);
    setPx((caixa.width - largura * escala) / 2);
    setPy(40);
  }

  // Ajusta uma vez, na primeira montagem com conteúdo — depois a câmera é do usuário.
  useEffect(() => {
    if (ajustou.current || todos.length <= 1) return;
    ajustou.current = true;
    requestAnimationFrame(ajustar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos.length]);

  /**
   * Segura o cartão recém-aberto no lugar em que ele estava.
   *
   * <p>Em {@code useLayoutEffect}, e não em {@code useEffect}: a compensação precisa
   * acontecer antes de o navegador pintar, senão o cartão pisca na posição errada.
   */
  useLayoutEffect(() => {
    const antes = ancora.current;
    if (!antes) return;
    ancora.current = null;
    const agora = todos.find((n) => n.chave === antes.chave);
    if (!agora) return;
    setPx((v) => v - (agora.x - antes.x) * z);
    setPy((v) => v - (agora.y - antes.y) * z);
  }, [todos, z]);

  /**
   * A roda precisa de listener não-passivo para poder cancelar a rolagem da página —
   * o React registra os dele como passivos, e ali o preventDefault é ignorado.
   */
  useEffect(() => {
    const alvo = palco.current;
    if (!alvo) return;
    const aoRodar = (e: WheelEvent) => {
      e.preventDefault();
      const caixa = alvo.getBoundingClientRect();
      const mx = e.clientX - caixa.left;
      const my = e.clientY - caixa.top;
      setZ((antes) => {
        const novo = Math.min(2.2, Math.max(0.06, antes * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
        // Zoom no ponteiro, e não no canto: é o que faz aproximar parecer aproximar.
        setPx((x) => mx - (mx - x) * (novo / antes));
        setPy((y) => my - (my - y) * (novo / antes));
        return novo;
      });
    };
    alvo.addEventListener('wheel', aoRodar, { passive: false });
    return () => alvo.removeEventListener('wheel', aoRodar);
  }, []);

  useEffect(() => {
    if (!arrastando) return;
    let ultimoX = 0;
    let ultimoY = 0;
    let primeiro = true;
    const aoMover = (e: MouseEvent) => {
      if (primeiro) {
        ultimoX = e.clientX;
        ultimoY = e.clientY;
        primeiro = false;
        return;
      }
      setPx((x) => x + (e.clientX - ultimoX));
      setPy((y) => y + (e.clientY - ultimoY));
      ultimoX = e.clientX;
      ultimoY = e.clientY;
    };
    const aoSoltar = () => setArrastando(false);
    window.addEventListener('mousemove', aoMover);
    window.addEventListener('mouseup', aoSoltar);
    return () => {
      window.removeEventListener('mousemove', aoMover);
      window.removeEventListener('mouseup', aoSoltar);
    };
  }, [arrastando]);

  /* ------------------------------------------------------------- as curvas */

  const caminhos = useMemo(() => {
    const hierarquia: string[] = [];
    const escolhas: string[] = [];
    const dentro = new Set(visiveis.map((n) => n.chave));

    visiveis.forEach((no) => {
      if (estaRecolhido(no.chave)) return;
      no.filhos.forEach((f) => {
        if (!dentro.has(f.chave)) return;
        const x1 = no.x + LARGURA / 2;
        const y1 = no.y + ALTURA;
        const x2 = f.x + LARGURA / 2;
        const y2 = f.y;
        const meio = (y1 + y2) / 2;
        hierarquia.push(
          `<path d="M${x1},${y1} C${x1},${meio} ${x2},${meio} ${x2},${y2}" fill="none" ` +
            `stroke="${f.cor}" stroke-opacity=".5" stroke-width="1.6"/>`
        );
      });
    });

    visiveis.forEach((no) => {
      if (no.cenaId == null) return;
      no.destinos.forEach((destino) => {
        const alvo = posPorCena.get(destino);
        // Destino num ramo fechado não vira curva: ela sairia para lugar nenhum.
        if (!alvo || !dentro.has(alvo.chave)) return;
        const x1 = no.x + LARGURA / 2;
        const y1 = no.y + ALTURA - 4;
        const x2 = alvo.x + LARGURA / 2;
        const y2 = alvo.y + 4;
        escolhas.push(
          `<path d="M${x1},${y1} Q${(x1 + x2) / 2 + 80},${(y1 + y2) / 2} ${x2},${y2}" ` +
            `fill="none" stroke="#C9A25A" stroke-opacity=".45" stroke-width="1.3" ` +
            `stroke-dasharray="5 4"/>`
        );
      });
    });

    return hierarquia.join('') + escolhas.join('');
  }, [visiveis, posPorCena, estaRecolhido]);

  return (
    <div
      className={`cn-palco${arrastando ? ' arrastando' : ''}`}
      ref={palco}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('.cn-cartao, .cn-zoom, .cn-legenda')) return;
        setArrastando(true);
      }}
    >
      <div
        className="cn-canvas"
        style={{ transform: `translate(${px}px, ${py}px) scale(${z})` }}
      >
        <svg
          width={largura}
          height={altura}
          dangerouslySetInnerHTML={{ __html: caminhos }}
        />

        {visiveis.map((no) => {
          const quantos = no.filhos.length || no.ocultos;
          const fechado = estaRecolhido(no.chave);
          return (
            <div
              key={no.chave}
              className={`cn-cartao${selecionada === no.cenaId ? ' sel' : ''}`}
              style={{ left: no.x, top: no.y, ['--c' as string]: no.cor } as React.CSSProperties}
              onClick={() => no.cenaId != null && aoSelecionar(no.cenaId)}
              title={no.cenaId != null ? 'Abrir no editor' : no.rotulo}
            >
              {no.codigo && <span className="cn-cartao-cod">{no.codigo}</span>}
              <span className="cn-cartao-nome">{no.rotulo}</span>
              <span className="cn-cartao-meta">
                <span className="cn-cartao-selo">{no.selo}</span>
                {no.nota && (
                  <span className={`cn-cartao-nota${no.aviso ? ' aviso' : ''}`}>{no.nota}</span>
                )}
                {quantos > 0 && <span className="cn-cartao-conta">{quantos}</span>}
              </span>

              {quantos > 0 && (
                <button
                  type="button"
                  className="cn-cartao-tog"
                  onClick={(e) => {
                    e.stopPropagation();
                    ancora.current = { chave: no.chave, x: no.x, y: no.y };
                    aoRecolher(no.chave, !fechado);
                  }}
                  title={fechado ? 'Abrir este ramo' : 'Recolher este ramo'}
                >
                  {fechado ? '+' : '−'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="cn-zoom">
        <button type="button" onClick={() => setZ((v) => Math.max(0.06, v / 1.2))} title="Afastar">
          −
        </button>
        <button type="button" onClick={() => setZ((v) => Math.min(2.2, v * 1.2))} title="Aproximar">
          +
        </button>
        <button type="button" className="largo" onClick={ajustar}>
          AJUSTAR
        </button>
        <span className="cn-zoom-pct">{Math.round(z * 100)}%</span>
      </div>

      <div className="cn-legenda">
        {TIPOS_DE_CENA.map((t) => (
          <span className="cn-leg" key={t}>
            <span className="sw" style={{ background: COR_DO_TIPO[t] }} />
            {ROTULO_DO_TIPO[t]}
          </span>
        ))}
        <span className="cn-leg">
          <span className="sw tracejado" />
          ESCOLHA
        </span>
      </div>
    </div>
  );
}
